import { useMemo, useCallback } from 'react';
import type { TreeDataNode } from 'antd';

interface UseFilteredTreeDataProps {
  treeData: TreeDataNode[];
  searchValue: string;
  showSearch?: boolean;
  renderTreeNodeTitle: (nodeData: TreeDataNode, originalTitle: string) => React.ReactNode;
}

export const useFilteredTreeData = ({
  treeData,
  searchValue,
  showSearch = false,
  renderTreeNodeTitle
}: UseFilteredTreeDataProps) => {
  const enhanceTreeData = useCallback(
    (nodes: TreeDataNode[]): TreeDataNode[] => {
      return nodes.map(node => {
        const nodeTitle = node.title as string;
        const enhancedNode: TreeDataNode = {
          ...node,
          title: renderTreeNodeTitle(node, nodeTitle),
          children: node.children ? enhanceTreeData(node.children) : undefined
        };
        // Store original title for filtering using a custom property
        (enhancedNode as any).originalTitle = node.title;
        return enhancedNode;
      });
    },
    [renderTreeNodeTitle]
  );

  const { filteredTreeData, searchMatchKeys } = useMemo(() => {
    let dataToUse = treeData;
    const matchedKeys: string[] = [];

    if (showSearch && searchValue.trim()) {
      const filterNodes = (nodes: TreeDataNode[]): TreeDataNode[] => {
        return nodes.reduce((filtered: TreeDataNode[], node) => {
          const searchText = (node as any).searchText;
          const matchesSearch = searchText && searchText.toLowerCase().includes(searchValue.toLowerCase());
          const filteredChildren = node.children ? filterNodes(node.children) : [];

          if (matchesSearch || filteredChildren.length > 0) {
            // If this node matches the search, add its key to searchMatchKeys
            if (matchesSearch && node.key) {
              matchedKeys.push(String(node.key));
            }

            filtered.push({
              ...node,
              children: filteredChildren.length > 0 ? filteredChildren : node.children
            });
          }

          return filtered;
        }, []);
      };

      dataToUse = filterNodes(treeData);
    }

    return {
      filteredTreeData: enhanceTreeData(dataToUse),
      searchMatchKeys: matchedKeys
    };
  }, [treeData, searchValue, showSearch, enhanceTreeData]);

  return { filteredTreeData, searchMatchKeys };
};
